import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { UploadSimple, CheckCircle, PencilSimple, Trash } from "@phosphor-icons/react";

export default function LoanDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [loan, setLoan] = useState(null);
    const [busy, setBusy] = useState(false);
    const [extractPreview, setExtractPreview] = useState(null); // {rows, note, document_id}
    const [editOpen, setEditOpen] = useState(false);
    const [intSchedule, setIntSchedule] = useState(null); // {rows, total_accrued, total_due} for OD loans

    const load = async () => {
        try {
            const { data } = await api.get(`/loans/${id}`);
            setLoan(data);
            if (data.loan_type === "od") {
                try {
                    const s = await api.get(`/loans/${id}/interest-schedule`);
                    setIntSchedule(s.data);
                } catch (_e) { setIntSchedule(null); }
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [id]);

    const accrueMonth = async (month) => {
        try {
            await api.post(`/loans/${id}/accrue-month`, null, { params: { month }});
            toast.success(`${month} interest accrued`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const backfillAll = async () => {
        const dueMonths = (intSchedule?.rows || []).filter((r) => r.status === "due").map((r) => r.month);
        if (dueMonths.length === 0) { toast.info("Nothing to backfill — all months are accrued"); return; }
        if (!window.confirm(`Post ${dueMonths.length} monthly interest accrual${dueMonths.length > 1 ? "s" : ""} (${dueMonths[0]} → ${dueMonths[dueMonths.length - 1]})?`)) return;
        try {
            const { data } = await api.post(`/loans/${id}/backfill`, { months: dueMonths });
            toast.success(`Backfilled ${data.posted_count} · skipped ${data.skipped_count}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const uploadSchedule = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post(`/loans/${id}/amortization/extract`, fd, { headers: { "Content-Type": "multipart/form-data" }});
            setExtractPreview(data);
            toast.success(`Extracted ${data.rows.length} rows — review and confirm`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const saveSchedule = async () => {
        if (!extractPreview) return;
        setBusy(true);
        try {
            await api.post(`/loans/${id}/amortization/save`, {
                rows: extractPreview.rows,
                document_id: extractPreview.document_id,
            });
            toast.success("Amortization schedule saved");
            setExtractPreview(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const closeLoan = async () => {
        const reason = window.prompt("Reason for closing?");
        if (!reason) return;
        try {
            await api.post(`/loans/${id}/close`, null, { params: { reason }});
            toast.success("Loan closed");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const deleteLoan = async () => {
        if (!window.confirm(`Delete "${loan.name}" permanently? This also reverses its opening ledger entry. (Only allowed if no payments exist.)`)) return;
        try {
            await api.delete(`/loans/${id}`);
            toast.success("Loan deleted");
            nav("/loans");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const markFundsUsedUp = async () => {
        const outstanding = loan.principal_outstanding || loan.settlement_offer || 0;
        if (!window.confirm(`Mark ${loan.name}'s proceeds as already consumed?\n\nThis re-books the opening entry: Dr "Loans Utilised — Pre-Onboarding" ₹${inr(outstanding)} · Cr Loan Payable ₹${inr(outstanding)}.\n\nNet Worth will DROP by ₹${inr(outstanding)}. Only tick this if the money was spent before you started using this system.`)) return;
        try {
            await api.post(`/loans/${id}/mark-funds-used-up`);
            toast.success("Opening entry reclassified — Net Worth adjusted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (!loan) return <div className="p-8">Loading…</div>;
    const rows = loan.amortization_rows || [];
    const paidCount = rows.filter((r) => r.paid).length;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title={loan.name}
                sub={<span>{loan.loan_type === "settlement" ? "Old loan under settlement" : loan.loan_type === "od" ? "OD (interest-only)" : "Term loan"} · Lender: {loan.lender_name || "—"} · <StatusChip status={loan.status} /></span>}
                right={
                    <div className="flex items-center gap-2">
                        {loan.status === "active" ? (
                            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-loan-btn"><PencilSimple size={14} className="mr-2" />Edit</Button>
                        ) : null}
                        <Button variant="outline" onClick={() => nav("/loans")}>Back to Loans</Button>
                    </div>
                }
            />
            <div className="p-8 space-y-6">
                {loan.waiver_notes ? (
                    <div className="border-l-4 border-amber-400 bg-amber-50 rounded-md px-4 py-3 text-sm text-amber-900"
                         data-testid="loan-waiver-banner">
                        <div className="font-medium mb-0.5">⚠ Waiver / write-off flag — needs CA review</div>
                        <div className="whitespace-pre-wrap text-[13px]">{loan.waiver_notes}</div>
                        <div className="text-[11px] mt-1 text-amber-800">
                            No auto-journal is booked from this note. Any forgiven principal may be taxable other-income — ask your CA before booking it.
                        </div>
                    </div>
                ) : null}

                {loan.status === "active" && (loan.loan_type === "od" || loan.loan_type === "term") ? (
                    loan.funds_used_up ? (
                        <div className="border border-rose-200 bg-rose-50 rounded-md px-4 py-2 text-[12px] text-rose-900 flex items-center gap-2"
                             data-testid="funds-used-up-banner">
                            <span className="font-medium">Marked: proceeds already consumed</span>
                            <span className="text-stone-600">· Opening entry debits <b>Loans Utilised — Pre-Onboarding</b> · Net Worth reflects this loan as reducing wealth by ₹{inr(loan.principal_outstanding || 0)}.</span>
                        </div>
                    ) : (
                        <div className="border border-stone-200 bg-stone-50 rounded-md px-4 py-3 text-sm flex items-center justify-between"
                             data-testid="funds-status-banner">
                            <div className="text-[13px] text-stone-700">
                                <b>Loan proceeds status:</b> money is assumed to still be in the bank (parked in <b>Suspense</b>). If it was consumed as expenses before onboarding, mark it used-up so Net Worth reflects the real position.
                            </div>
                            <Button size="sm" variant="outline" onClick={markFundsUsedUp} data-testid="mark-funds-used-up-btn">
                                Mark as used up
                            </Button>
                        </div>
                    )
                ) : null}
                <div className="grid grid-cols-3 gap-4">
                    {loan.loan_type === "settlement" ? (
                        <>
                            <MetricCard label="Original principal" value={loan.original_principal} tone="dark" />
                            <MetricCard label="Settlement offer" value={loan.settlement_offer} tone="rose" />
                            <MetricCard label="Remaining to pay" value={loan.settlement_remaining} tone="rose" bold />
                        </>
                    ) : loan.loan_type === "od" ? (
                        <>
                            <MetricCard label="Principal outstanding" value={loan.principal_outstanding} tone="teal" bold />
                            <MetricCard label="Monthly interest" value={loan.monthly_interest_amount} tone="dark" />
                            <MetricCard label="Interest payable (unpaid)" value={intSchedule?.total_accrued || 0} tone="rose" bold />
                        </>
                    ) : (
                        <>
                            <MetricCard label="Principal outstanding" value={loan.principal_outstanding} tone="teal" bold />
                            <MetricCard label="EMI amount" value={loan.emi_amount} tone="dark" />
                            <MetricCard label="Rows paid" value={rows.length ? `${paidCount} / ${rows.length}` : "—"} tone="dark" isText />
                        </>
                    )}
                </div>

                {loan.loan_type === "term" ? (
                    <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="amortization-card">
                        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                            <div>
                                <div className="font-serif text-lg">Amortization schedule</div>
                                <div className="text-xs text-stone-500">
                                    {rows.length ? `${rows.length} rows · ${paidCount} paid` : "Upload the bank's schedule (PDF or Excel). AI will extract the rows — you review and confirm."}
                                </div>
                            </div>
                            {loan.status === "active" ? (
                                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-sm text-sm border cursor-pointer hover:bg-stone-50" style={{ borderColor: "var(--se-border)" }} data-testid="upload-schedule-btn">
                                    <UploadSimple size={14} /> {busy ? "Reading…" : "Upload schedule"}
                                    <input type="file" accept=".pdf,.xls,.xlsx,.csv,image/*" className="hidden" onChange={(e) => uploadSchedule(e.target.files?.[0])} disabled={busy} />
                                </label>
                            ) : null}
                        </div>

                        {extractPreview ? (
                            <div className="p-4 bg-amber-50 border-b" style={{ borderColor: "var(--se-border)" }}>
                                <div className="text-sm font-medium mb-2">Review AI-extracted rows ({extractPreview.rows.length})</div>
                                {extractPreview.note ? <div className="text-[11px] text-amber-700 mb-2">{extractPreview.note}</div> : null}
                                <div className="max-h-72 overflow-y-auto bg-white border rounded-sm" style={{ borderColor: "var(--se-border)" }}>
                                    <table className="se-table text-xs" data-testid="preview-table">
                                        <thead><tr><th>#</th><th>Due date</th><th>Principal</th><th>Interest</th><th>Balance</th></tr></thead>
                                        <tbody>
                                            {extractPreview.rows.map((r, i) => (
                                                <tr key={i}>
                                                    <td>{r.month_no || (i + 1)}</td>
                                                    <td className="font-mono">{r.due_date || "—"}</td>
                                                    <td className="se-num">₹ {inr(r.principal)}</td>
                                                    <td className="se-num">₹ {inr(r.interest)}</td>
                                                    <td className="se-num">₹ {inr(r.balance)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <Button variant="outline" onClick={() => setExtractPreview(null)}>Discard</Button>
                                    <Button onClick={saveSchedule} disabled={busy} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="confirm-schedule">
                                        <CheckCircle size={14} className="mr-2" /> Confirm and save
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        {rows.length ? (
                            <table className="se-table" data-testid="schedule-table">
                                <thead><tr><th>#</th><th>Due date</th><th>Principal</th><th>Interest</th><th>Balance</th><th>Status</th></tr></thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.month_no} style={r.paid ? { opacity: 0.55 } : undefined}>
                                            <td>{r.month_no}</td>
                                            <td className="font-mono text-xs">{r.due_date || "—"}</td>
                                            <td className="se-num">₹ {inr(r.principal)}</td>
                                            <td className="se-num">₹ {inr(r.interest)}</td>
                                            <td className="se-num text-stone-500">₹ {inr(r.balance)}</td>
                                            <td>{r.paid ? <span className="se-chip se-chip-posted text-[10px]">paid</span> : <span className="se-chip se-chip-draft text-[10px]">due</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : null}
                    </div>
                ) : null}

                {loan.loan_type === "od" && intSchedule ? (
                    <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="od-interest-card">
                        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                            <div>
                                <div className="font-serif text-lg">Monthly interest schedule</div>
                                <div className="text-xs text-stone-500">
                                    Accrued each month-end into <b>Interest Payable — {loan.name}</b>. Principal stays untouched.
                                </div>
                            </div>
                            {loan.status === "active" && intSchedule.rows.some((r) => r.status === "due") ? (
                                <Button size="sm" onClick={backfillAll} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="od-int-backfill-btn">
                                    Backfill {intSchedule.rows.filter((r) => r.status === "due").length} month{intSchedule.rows.filter((r) => r.status === "due").length > 1 ? "s" : ""}
                                </Button>
                            ) : null}
                        </div>
                        {intSchedule.rows.length === 0 ? (
                            <div className="p-6 text-center text-stone-500 text-sm">No months to accrue yet — this loan was opened this month.</div>
                        ) : (
                            <table className="se-table" data-testid="od-int-table">
                                <thead><tr><th>Month</th><th className="text-right">Amount</th><th>Accrued on</th><th>Status</th><th className="text-right">Action</th></tr></thead>
                                <tbody>
                                    {intSchedule.rows.map((r) => (
                                        <tr key={r.month} data-testid={`od-int-row-${r.month}`}>
                                            <td className="font-mono text-xs">{r.month}</td>
                                            <td className="se-num">₹ {inr(r.amount)}</td>
                                            <td className="font-mono text-xs text-stone-500">{r.accrual_date || "—"}</td>
                                            <td>
                                                {r.status === "accrued" ? (
                                                    <span className="se-chip se-chip-posted text-[10px]">accrued</span>
                                                ) : (
                                                    <span className="se-chip se-chip-draft text-[10px]">due</span>
                                                )}
                                            </td>
                                            <td className="text-right">
                                                {r.status === "due" && loan.status === "active" ? (
                                                    <Button size="sm" variant="outline" onClick={() => accrueMonth(r.month)} data-testid={`od-int-accrue-${r.month}`}>
                                                        Accrue
                                                    </Button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t" style={{ borderColor: "var(--se-border)" }}>
                                        <td className="font-medium">Total accrued</td>
                                        <td className="se-num font-medium">₹ {inr(intSchedule.total_accrued)}</td>
                                        <td colSpan="3" className="text-xs text-stone-500">
                                            {intSchedule.total_due > 0 ? <>· ₹ {inr(intSchedule.total_due)} pending backfill</> : "· all months accrued"}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>
                ) : null}

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>Payment history</div>
                    <table className="se-table" data-testid="payments-table">
                        <thead><tr><th>Date</th><th>Amount</th><th>Interest</th><th>Principal</th><th>Settlement</th><th>Source</th></tr></thead>
                        <tbody>
                            {(loan.payments || []).length === 0 ? <tr><td colSpan="6" className="py-6 text-center text-stone-500">No payments yet.</td></tr> :
                                (loan.payments || []).map((p) => (
                                    <tr key={p.id} data-testid={`payment-${p.id}`}>
                                        <td className="font-mono text-xs">{p.date}</td>
                                        <td className="se-num font-medium">₹ {inr(p.amount)}</td>
                                        <td className="se-num text-stone-500">{p.split.interest > 0 ? `₹ ${inr(p.split.interest)}` : "—"}</td>
                                        <td className="se-num text-stone-500">{p.split.principal > 0 ? `₹ ${inr(p.split.principal)}` : "—"}</td>
                                        <td className="se-num text-stone-500">{p.split.settlement > 0 ? `₹ ${inr(p.split.settlement)}` : "—"}</td>
                                        <td className="text-xs text-stone-500">{p.source_type}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>

                {loan.status === "active" ? (
                    <div className="flex justify-between items-center">
                        {(loan.payments || []).length === 0 ? (
                            <Button variant="outline" onClick={deleteLoan} style={{ borderColor: "#78716c", color: "#57534e" }} data-testid="delete-loan-btn"><Trash size={14} className="mr-2" />Delete loan</Button>
                        ) : <div />}
                        <Button variant="outline" onClick={closeLoan} style={{ borderColor: "#e11d48", color: "#9f1239" }} data-testid="close-loan-btn">Close loan</Button>
                    </div>
                ) : null}
            </div>

            <EditLoanDialog open={editOpen} onOpenChange={setEditOpen} loan={loan} onSaved={load} />
        </>
    );
}

function MetricCard({ label, value, tone = "dark", bold = false, isText = false }) {
    const bg = tone === "rose" ? "rgba(225, 29, 72, 0.05)" : tone === "teal" ? "rgba(20, 184, 166, 0.05)" : "#fff";
    const color = tone === "rose" ? "#9f1239" : tone === "teal" ? "#0f766e" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4" style={{ borderColor: "var(--se-border)", background: bg }}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num text-2xl mt-1 ${bold ? "font-medium" : ""}`} style={{ color }}>
                {isText ? value : `₹ ${inr(value || 0)}`}
            </div>
        </div>
    );
}


function EditLoanDialog({ open, onOpenChange, loan, onSaved }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && loan) {
            setForm({
                name: loan.name || "",
                lender_name: loan.lender_name || "",
                opened_on: loan.opened_on || "",
                original_principal: loan.original_principal ?? "",
                settlement_offer: loan.settlement_offer ?? "",
                monthly_interest_amount: loan.monthly_interest_amount ?? "",
                principal_outstanding: loan.principal_outstanding ?? "",
                annual_rate_pct: loan.annual_rate_pct ?? "",
                tenure_months: loan.tenure_months ?? "",
                emi_start_date: loan.emi_start_date || "",
                emi_amount: loan.emi_amount ?? "",
                waiver_notes: loan.waiver_notes || "",
            });
        }
    }, [open, loan]);

    if (!loan) return null;
    const hasPayments = (loan.payments || []).length > 0;
    const principalLocked = hasPayments && (loan.loan_type === "od" || loan.loan_type === "term");

    const save = async () => {
        const body = {};
        // Send only fields that changed
        const numericKeys = ["original_principal","settlement_offer","monthly_interest_amount","principal_outstanding","annual_rate_pct","emi_amount"];
        for (const k of Object.keys(form)) {
            let v = form[k];
            // Special case: waiver_notes can be intentionally cleared to remove the flag.
            if (k === "waiver_notes") {
                const cur = loan.waiver_notes || "";
                const next = (v || "").trim();
                if (next !== cur) body[k] = next; // send even "" to clear
                continue;
            }
            if (v === "" || v === null || v === undefined) continue;
            if (numericKeys.includes(k)) v = Number(v);
            else if (k === "tenure_months") v = parseInt(v, 10);
            if (v !== loan[k]) body[k] = v;
        }
        if (Object.keys(body).length === 0) {
            toast.info("No changes to save");
            return;
        }
        if (!form.name || !form.name.trim()) return toast.error("Name cannot be empty");
        setSaving(true);
        try {
            await api.patch(`/loans/${loan.id}`, body);
            toast.success("Loan updated");
            onOpenChange(false);
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="edit-loan-dialog">
                <DialogHeader><DialogTitle>Edit loan — {loan.name}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    {hasPayments ? (
                        <div className="text-[11px] p-3 rounded-sm bg-amber-50 border" style={{ borderColor: "var(--se-border)" }}>
                            This loan has {loan.payments.length} payment(s) posted. You can still fix lender info, rate, tenure and EMI details — but principal outstanding is locked to preserve the ledger. If the principal is wrong, close & re-create the loan.
                        </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Loan name *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-loan-name" /></div>
                        <div><Label>Lender</Label><Input value={form.lender_name || ""} onChange={(e) => setForm({ ...form, lender_name: e.target.value })} data-testid="edit-loan-lender" /></div>
                        <div><Label>Opened on</Label><Input type="date" value={form.opened_on || ""} onChange={(e) => setForm({ ...form, opened_on: e.target.value })} data-testid="edit-loan-opened-on" /></div>
                    </div>

                    {loan.loan_type === "settlement" ? (
                        <div className="grid grid-cols-2 gap-4 border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
                            <div><Label>Original principal (₹)</Label><Input type="number" value={form.original_principal ?? ""} onChange={(e) => setForm({ ...form, original_principal: e.target.value })} data-testid="edit-loan-orig-principal" /></div>
                            <div><Label>Settlement offer (₹)</Label><Input type="number" value={form.settlement_offer ?? ""} onChange={(e) => setForm({ ...form, settlement_offer: e.target.value })} data-testid="edit-loan-settle-offer" /></div>
                            <p className="col-span-2 text-[11px] text-stone-500">New offer can&apos;t drop below what has already been paid (₹{inr(loan.settlement_paid || 0)}).</p>
                        </div>
                    ) : loan.loan_type === "od" ? (
                        <div className="grid grid-cols-2 gap-4 border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
                            <div>
                                <Label>Principal outstanding (₹) {principalLocked ? <span className="text-amber-700 text-[10px]">(locked)</span> : null}</Label>
                                <Input type="number" disabled={principalLocked} value={form.principal_outstanding ?? ""} onChange={(e) => setForm({ ...form, principal_outstanding: e.target.value })} data-testid="edit-loan-od-principal" />
                            </div>
                            <div><Label>Monthly interest (₹)</Label><Input type="number" value={form.monthly_interest_amount ?? ""} onChange={(e) => setForm({ ...form, monthly_interest_amount: e.target.value })} data-testid="edit-loan-od-int" /></div>
                            {!principalLocked ? <p className="col-span-2 text-[11px] text-stone-500">Changing principal re-books the opening ledger entry.</p> : null}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
                            <div>
                                <Label>Principal outstanding (₹) {principalLocked ? <span className="text-amber-700 text-[10px]">(locked)</span> : null}</Label>
                                <Input type="number" disabled={principalLocked} value={form.principal_outstanding ?? ""} onChange={(e) => setForm({ ...form, principal_outstanding: e.target.value })} data-testid="edit-loan-term-principal" />
                            </div>
                            <div><Label>EMI amount (₹)</Label><Input type="number" value={form.emi_amount ?? ""} onChange={(e) => setForm({ ...form, emi_amount: e.target.value })} data-testid="edit-loan-emi" /></div>
                            <div><Label>Annual rate (%)</Label><Input type="number" step="0.01" value={form.annual_rate_pct ?? ""} onChange={(e) => setForm({ ...form, annual_rate_pct: e.target.value })} data-testid="edit-loan-rate" /></div>
                            <div><Label>Tenure (months)</Label><Input type="number" value={form.tenure_months ?? ""} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} data-testid="edit-loan-tenure" /></div>
                            <div><Label>EMI start date</Label><Input type="date" value={form.emi_start_date || ""} onChange={(e) => setForm({ ...form, emi_start_date: e.target.value })} data-testid="edit-loan-emi-start" /></div>
                        </div>
                    )}

                    <div className="border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
                        <Label className="flex items-center gap-2">
                            <span>Waiver / write-off notes</span>
                            <span className="text-[10px] text-stone-500 font-normal">optional — review with CA</span>
                        </Label>
                        <Textarea
                            rows={3}
                            value={form.waiver_notes || ""}
                            onChange={(e) => setForm({ ...form, waiver_notes: e.target.value })}
                            placeholder="e.g. Lender waived ₹50,000 of principal on 15-Aug-2026 — no auto-journal; needs CA review for other-income tax treatment"
                            data-testid="edit-loan-waiver-notes"
                        />
                        <div className="text-[11px] text-stone-500 mt-1">
                            No journal is booked from this note. It flags the loan on reports so your CA reviews the tax treatment of any forgiven amount.
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={save} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="save-edit-loan">{saving ? "Saving…" : "Save changes"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
